import type {
  ReportLabel,
  ReportLabelsResponse,
  ReportLabelTarget,
} from '@clockwise/clients/report-labels';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { reportLabelsApi } from '@/lib/api';
import { apiErrorText } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

type LabelsCache = { data: ReportLabelsResponse };

/** The caller's labels and what is filed under them — one request per page. */
export function useReportLabels() {
  return useQuery({
    queryKey: queryKeys.reportLabels.all,
    queryFn: () => reportLabelsApi.list(),
  });
}

/** The key a cached item is matched on — a report id, or a built-in's key. */
function sameTarget(
  item: { reportId: string | null; builtinKey: string | null },
  t: ReportLabelTarget,
) {
  return 'reportId' in t ? item.reportId === t.reportId : item.builtinKey === t.builtinKey;
}

/**
 * File or unfile a report under a label — a star is the same call with
 * `labelId: 'starred'`.
 *
 * OPTIMISTIC: a tick in the picker or a click on the star is the kind of
 * action people do several of in a row, and waiting for each round trip makes
 * the list flicker under the pointer. On failure the cache rolls back and a
 * toast says what went wrong (G15) — there is no field to put it under.
 *
 * The very first star has no Starred label in the cache yet to file under, so
 * that one click waits for the server and refetches instead.
 */
export function useToggleLabelItem() {
  const queryClient = useQueryClient();
  const key = queryKeys.reportLabels.all;
  return useMutation({
    mutationKey: TOGGLE_KEY,
    mutationFn: ({ labelId, target, on }: Toggle) =>
      on ? reportLabelsApi.addItem(labelId, target) : reportLabelsApi.removeItem(labelId, target),
    onMutate: async (toggle) => {
      await queryClient.cancelQueries({ queryKey: key });
      if (toggle.labelId !== 'starred') applyToggle(queryClient, toggle);
    },
    // Undo THIS toggle only. Restoring a snapshot taken before it would also
    // throw away every tick made since, which then reappear on the refetch.
    onError: (err, toggle) => {
      if (toggle.labelId !== 'starred') applyToggle(queryClient, { ...toggle, on: !toggle.on });
      toast.error(apiErrorText(err, 'Couldn’t update the label. Try again.'));
    },
    // Refetch once the LAST toggle in flight settles. Refetching after each one
    // would land the server's answer to the first tick on top of the second
    // tick's optimistic state, unticking it until its own request returned.
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey: TOGGLE_KEY }) === 1) {
        return queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });
}

const TOGGLE_KEY = ['report-labels', 'toggle'] as const;

interface Toggle {
  labelId: string;
  target: ReportLabelTarget;
  on: boolean;
}

/** Put one item into, or take it out of, the cached label list. */
function applyToggle(
  queryClient: ReturnType<typeof useQueryClient>,
  { labelId, target, on }: Toggle,
) {
  const key = queryKeys.reportLabels.all;
  const current = queryClient.getQueryData<LabelsCache>(key);
  if (!current) return;
  const others = current.data.items.filter(
    (i) => !(i.labelId === labelId && sameTarget(i, target)),
  );
  const items = on
    ? [
        ...others,
        {
          labelId,
          reportId: 'reportId' in target ? target.reportId : null,
          builtinKey: 'builtinKey' in target ? target.builtinKey : null,
        },
      ]
    : others;
  queryClient.setQueryData<LabelsCache>(key, { data: { ...current.data, items } });
}

/** The Starred label's id if the caller has one, else the `starred` alias. */
export function starTargetId(labels: readonly ReportLabel[]): string {
  return labels.find((l) => l.kind === 'starred')?.id ?? 'starred';
}
